import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { camelizeKeys } from 'humps';

import { useHighlights } from 'features/common/hooks/useHighlights';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { getData } from 'services/api';
import { IdParamsType } from 'utils/types';
import { CapacityTicketType, EventCapacity } from './types';
import {
	RawPool,
	buildAmountByName,
	buildEventInfoById,
	buildExpiredByName,
	buildIssuedByName,
	buildPoolByMemberName,
	buildSalesIssuedByName,
	derivePools,
	deriveTotals,
	deriveTypes,
	resolveIssuedByName,
} from './deriveCapacity';

/**
 * Pool-aware capacity for the current event, and the single source every pool/capacity figure reads. Joins
 * four sources (event API, highlights, public ticket types, pools API) and resolves each ticket type against
 * its shared-capacity pool: `available = min(type_remaining, pool_remaining)`.
 *
 * This hook is orchestration only — fetch the four sources, then memoise the pure derivations in
 * `deriveCapacity.ts` (where every capacity rule lives). `compedByType` (from `useCompedInventory`) supplies
 * per-type comped counts for display; it does not affect availability, so capacity-only consumers (e.g. the
 * Fill Rate KPI) can omit it.
 */
export function useEventCapacity(compedByType: Record<string, number> = {}): EventCapacity {
	const { id } = useParams<IdParamsType>();
	const { event } = useGetEvent();
	const { eventHighlights, isLoading: highlightsLoading } = useHighlights(id);

	// Stable select identities so react-query returns the cached camelized array between renders instead of
	// a fresh object each render — otherwise the derive memos below (keyed on these arrays) recompute every
	// render and re-render all useEventCapacity consumers. camelizeKeys is a pure transform → empty deps.
	const selectTicketTypes = useCallback((data: unknown[]) => camelizeKeys(data) as unknown as CapacityTicketType[], []);
	const selectPools = useCallback((data: RawPool[]) => camelizeKeys(data) as unknown as RawPool[], []);

	// Public ticket types — the iteration source (one CapacityType per element).
	const { data: ticketTypesData, isLoading: typesLoading } = useQuery({
		queryKey: ['ticketTypes', id],
		queryFn: (): Promise<unknown[]> => getData(`/public/ticketing/ticket_types?event_id=${id}`),
		select: selectTicketTypes,
		enabled: !!id,
	});

	// Pools API — authoritative for pool total + members. Same query key as PoolsSection so the fetch is
	// deduplicated. Decoded with bare humps → `iD` (see RawPool in deriveCapacity.ts).
	const { data: poolsData = [] } = useQuery<RawPool[]>({
		queryKey: ['eventPools', id],
		queryFn: () => getData(`/events/${id}/pools`),
		select: selectPools,
		enabled: !!id,
	});

	const now = useMemo(() => new Date(), []);

	const issuedByName = useMemo(
		() => buildIssuedByName(eventHighlights?.ticket_types_inventory),
		[eventHighlights?.ticket_types_inventory]
	);
	const salesIssuedByName = useMemo(() => buildSalesIssuedByName(eventHighlights?.sales), [eventHighlights?.sales]);
	const amountByName = useMemo(() => buildAmountByName(ticketTypesData), [ticketTypesData]);
	const expiredByName = useMemo(() => buildExpiredByName(ticketTypesData, now), [ticketTypesData, now]);
	const resolvedIssuedByName = useMemo(
		() => resolveIssuedByName(ticketTypesData, issuedByName, salesIssuedByName),
		[ticketTypesData, issuedByName, salesIssuedByName]
	);
	const eventInfoById = useMemo(() => buildEventInfoById(event?.ticketTypes), [event?.ticketTypes]);

	const pools = useMemo(
		() => derivePools(poolsData, resolvedIssuedByName, amountByName, expiredByName),
		[poolsData, resolvedIssuedByName, amountByName, expiredByName]
	);
	const poolByMemberName = useMemo(() => buildPoolByMemberName(pools), [pools]);

	const types = useMemo(
		() =>
			deriveTypes(ticketTypesData, {
				eventInfoById,
				resolvedIssuedByName,
				expiredByName,
				poolByMemberName,
				compedByType,
			}),
		[ticketTypesData, eventInfoById, resolvedIssuedByName, expiredByName, poolByMemberName, compedByType]
	);

	const totals = useMemo(
		() =>
			deriveTotals(types, pools, {
				sold: eventHighlights?.tickets_sold ?? 0,
				comped: eventHighlights?.tickets_comped ?? 0,
			}),
		[types, pools, eventHighlights?.tickets_sold, eventHighlights?.tickets_comped]
	);

	return {
		types,
		pools,
		hasPools: pools.length > 0,
		isSeated: !!event?.seatsEventKey,
		totals,
		isLoading: highlightsLoading || typesLoading,
	};
}
